import { FormGroup } from '@angular/forms';
import { IFormControl } from './formcontrol.interface';
import { IFormFieldMergeConfig } from './form-field-merge-config.interface';
import { TranslateService } from '@ngx-translate/core';

/** Merges/splits form fields based on configuration. Pure; no DI. */
export class FormFieldMergeTransformer {

  static findMatchingConfig(
    fields: IFormControl[],
    configs: IFormFieldMergeConfig[]
  ): IFormFieldMergeConfig | null {
    if (!fields || !configs) return null;

    const fieldIds = new Set(fields.map(f => f.id));
    for (const config of configs) {
      const lookupId = config.beFieldId || config.valueFieldId;
      if (config.constantPrefix) {
        if (!fieldIds.has(lookupId)) continue;
        if (config.triggerFieldId && !fieldIds.has(config.triggerFieldId)) continue;
        return config;
      }
      if (config.prefixFieldId && fieldIds.has(config.prefixFieldId) && fieldIds.has(lookupId)) {
        return config;
      }
    }
    return null;
  }

  static isTriggerActive(
    payload: { [key: string]: any },
    config: IFormFieldMergeConfig
  ): boolean {
    if (!config.triggerFieldId) return true;
    return payload && payload[config.triggerFieldId] === config.triggerValue;
  }

  static mergeFields(
    fields: IFormControl[],
    config: IFormFieldMergeConfig,
    translate?: TranslateService
  ): IFormControl[] {
    if (!fields || !config) return fields;

    const cloned = fields.map(f => ({ ...f, validators: [...f.validators] }));
    const valueField = cloned.find(f => f.id === (config.beFieldId || config.valueFieldId));
    if (!valueField) return cloned;
    if (config.beFieldId) valueField.id = config.valueFieldId;

    valueField.validators = [...config.mergedValidators];
    valueField.type = config.mergedInputType;

    if (config.mergedHint != null) {
      valueField.hint = translate && !config.mergeHintTranslated ? translate.instant(config.mergedHint) : config.mergedHint;
    }
    if (config.mergedLabel != null) {
      valueField.label = config.mergedLabel;
    }
    if (config.mergedLabelSuffix != null && valueField.label) {
      valueField.label = `${valueField.label} (${config.mergedLabelSuffix})`;
    }
    if (config.mergedPlaceholder != null) {
      valueField.placeholder = config.mergedPlaceholder;
    }
    if (config.mergedPattern != null) {
      valueField.pattern = config.mergedPattern;
    }
    if (config.mergedVisualMask != null) {
      valueField.visualMask = config.mergedVisualMask;
      // Paste-time normalization: routes raw clipboard text through the same normalize() that the rest of the merge pipeline uses.
      valueField.visualMaskPrepare = config.normalize;
      // rmt-input's *ngIf ladder picks `patternForMask` (ngx-mask) over `visualMask` when both are truthy.
      // ngx-mask is what the visual mask is meant to replace (it counts the space as a real character), so clear any BE-provided pattern.
      valueField.pattern = undefined;
    }

    if (config.constantPrefix) {
      const triggerField = config.triggerFieldId ? cloned.find(f => f.id === config.triggerFieldId) : null;
      const triggerActive = !config.triggerFieldId || (triggerField && triggerField.value === config.triggerValue);
      if (triggerActive && valueField.value) {
        valueField.value = config.normalize(valueField.value as string);
      }
      return cloned;
    }

    const prefixField = cloned.find(f => f.id === config.prefixFieldId);
    if (!prefixField) return cloned;

    if (prefixField.value && valueField.value) {
      valueField.value = FormFieldMergeTransformer.combineForDisplay(
        prefixField.value, valueField.value
      );
    }

    return cloned.filter(f => f.id !== config.prefixFieldId);
  }

  /** Runs the config's normalize fn on raw user input. */
  static normalizeInput(rawInput: string, config: IFormFieldMergeConfig): string {
    if (!rawInput || !config) return rawInput || '';
    return config.normalize(rawInput);
  }

  /** Splits a merged value back into { prefixFieldId, valueFieldId } for the BE payload (BE-prefix mode only). */
  static splitForPayload(
    mergedValue: string,
    config: IFormFieldMergeConfig
  ): { [fieldId: string]: string } {
    if (config.constantPrefix) {
      return { [config.valueFieldId]: (config.constantPrefix + (mergedValue || '')) };
    }
    const prefixFieldId = config.prefixFieldId as string;
    if (!mergedValue || !config) {
      return {
        [prefixFieldId]: '',
        [config.valueFieldId]: mergedValue || ''
      };
    }

    const splitResult = config.split(mergedValue);
    return {
      [prefixFieldId]: splitResult.prefix,
      [config.valueFieldId]: splitResult.value
    };
  }

  static transformPayload(
    payload: { [key: string]: any },
    config: IFormFieldMergeConfig
  ): { [key: string]: any } {
    if (!payload || !config) return payload;

    const result = { ...payload };
    const rawValue = result[config.valueFieldId];
    if (!rawValue) return result;

    if (config.constantPrefix) {
      if (!FormFieldMergeTransformer.isTriggerActive(result, config)) return result;
      const normalized = config.normalize(rawValue);
      result[config.valueFieldId] = config.constantPrefix + normalized;
      return result;
    }

    const normalized = config.normalize(rawValue);
    const splitResult = config.split(normalized);
    result[config.prefixFieldId as string] = splitResult.prefix;
    result[config.valueFieldId] = splitResult.value;
    return result;
  }

  /** Concatenates prefix + value for display pre-population. */
  static combineForDisplay(prefix: string, value: string): string {
    return (prefix || '') + (value || '');
  }

  static handleFieldChange(
    fieldId: string,
    value: any,
    form: FormGroup,
    config: IFormFieldMergeConfig
  ): void {
    if (!form || !config) return;
    if (fieldId !== config.valueFieldId) return;
    if (typeof value !== 'string') return;

    if (config.triggerFieldId) {
      const triggerControl = form.controls[config.triggerFieldId];
      if (!triggerControl || triggerControl.value !== config.triggerValue) return;
    }

    const control = form.controls[config.valueFieldId];
    if (!control) return;

    const normalized = config.normalize(value);
    if (normalized !== value) {
      control.setValue(normalized, { emitEvent: false });
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/formbuilder/form-field-merge-transformer.ts